import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getStatusBadgeClasses as getSharedStatusBadgeClasses,
  getStatusLabel as getSharedStatusLabel,
} from '@/shared/utils/statusBadge';
import { getQmsCategoryApiService } from '@epics/qmsManagement/api/getQmsCategoryApiService';
import { formatQmsCategoryCreatedAt } from '../categoryList/qmsCategoryListValidation';
import type { QmsCategory } from '../../types/qmsCategoryTypes';

const orDash = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  return value.trim() ? value : '—';
};

export interface UseViewQmsCategoryReturn {
  category: QmsCategory | undefined;
  isLoading: boolean;
  showErrorScreen: boolean;
  notFound: boolean;
  accessError: boolean;
  statusLabel: string;
  statusBadgeClasses: { container: string; dot: string };
  formattedDescription: string;
  formattedCreatedAt: string;
  formattedUpdatedAt: string;
  refetch: () => Promise<void>;
}

export const useViewQmsCategory = (
  categoryId: string
): UseViewQmsCategoryReturn => {
  const [category, setCategory] = useState<QmsCategory | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [showErrorScreen, setShowErrorScreen] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [accessError, setAccessError] = useState(false);

  const fetchCategory = useCallback(async () => {
    setIsLoading(true);
    setNotFound(false);
    setAccessError(false);
    try {
      const response = await getQmsCategoryApiService.getCategory(categoryId);
      if (response.success && response.data) {
        setCategory(response.data);
        setShowErrorScreen(false);
      } else if (response.forbidden) {
        setAccessError(true);
      } else if (response.notFound) {
        setNotFound(true);
        setShowErrorScreen(false);
      } else {
        setShowErrorScreen(true);
      }
    } catch {
      setShowErrorScreen(true);
    } finally {
      setIsLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  const statusLabel = useMemo(
    () => getSharedStatusLabel(category?.status),
    [category?.status]
  );

  const statusBadgeClasses = useMemo(
    () => getSharedStatusBadgeClasses(category?.status),
    [category?.status]
  );

  const formattedDescription = useMemo(
    () => orDash(category?.description),
    [category?.description]
  );

  const formattedCreatedAt = useMemo(
    () =>
      category?.createdAt
        ? formatQmsCategoryCreatedAt(category.createdAt)
        : '—',
    [category?.createdAt]
  );

  const formattedUpdatedAt = useMemo(
    () =>
      category?.updatedAt
        ? formatQmsCategoryCreatedAt(category.updatedAt)
        : '—',
    [category?.updatedAt]
  );

  return {
    category,
    isLoading,
    showErrorScreen,
    notFound,
    accessError,
    statusLabel,
    statusBadgeClasses,
    formattedDescription,
    formattedCreatedAt,
    formattedUpdatedAt,
    refetch: fetchCategory,
  };
};
